/**
 * OfertaDeInstalar.tsx — la banda que ofrece instalar la primera vez.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ POR QUÉ EXISTE, Y POR QUÉ NO ES UN POPUP MODAL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Chrome quitó su mini-infobar de instalación en 2019 porque la gente la
 * cerraba sin leer. En Books quedaba sólo nuestro botón «Instalar» en la
 * topbar, que si no lo buscás no lo ves — y lo encontró César al no notar
 * nunca que se podía instalar en Chrome (en Edge sí, porque Edge insiste).
 *
 * La banda es el término medio: una franja ARRIBA DEL CONTENIDO que se cierra
 * con un botón y queda guardada por siete días. No bloquea, no interrumpe un
 * asiento a medio teclear, y la segunda vez que la cierra queda invisible por
 * mucho tiempo — el mismo criterio del aviso de versión.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶▶ POR QUÉ 7 DÍAS Y NO «PARA SIEMPRE»
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Para siempre haría que un «Más tarde» se vuelva «Nunca», y nadie volvería a
 * ver que esto se puede instalar. Siete días es el ciclo típico de uso de un
 * contador —cierra el mes y la semana siguiente lo revisa—, así que la banda
 * vuelve en el siguiente ciclo de atención, no al día siguiente.
 *
 * Y se guarda en `localStorage` porque es una preferencia de ESTA máquina:
 * una persona que entra desde el escritorio y el móvil puede querer la banda
 * en uno pero no en el otro.
 */
import { useEffect, useState } from 'react';
import {
  alCambiarInstalacion, instalar, sePuedeInstalar, yaInstalada,
} from '../nucleo/pwa.ts';
import { useIdioma } from '../nucleo/idioma.tsx';

const LLAVE_LS = 'skoped.books.oferta-instalar.posponer-hasta';
const SIETE_DIAS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * ¿Está pospuesta la banda? localStorage puede fallar en incógnito o si el
 * sitio no tiene permiso — se cae a «no está pospuesta» para que la banda
 * aparezca, que es el error seguro: enseñar demás es menos malo que esconder.
 */
function pospuesta(): boolean {
  try {
    const crudo = localStorage.getItem(LLAVE_LS);
    if (!crudo) return false;
    const hasta = Number.parseInt(crudo, 10);
    return Number.isFinite(hasta) && Date.now() < hasta;
  } catch {
    return false;
  }
}

function posponer() {
  try {
    localStorage.setItem(LLAVE_LS, String(Date.now() + SIETE_DIAS_MS));
  } catch {
    // Ignorar: la banda volverá a aparecer en la próxima carga. No es un
    // error del usuario — ver incógnito y los permisos de almacenamiento.
  }
}

export function OfertaDeInstalar() {
  const { t } = useIdioma();
  const [puede, setPuede] = useState(sePuedeInstalar());
  const [instalada, setInstalada] = useState(yaInstalada());
  const [cerrada, setCerrada] = useState(pospuesta());

  useEffect(() => alCambiarInstalacion((p) => {
    setPuede(p);
    setInstalada(yaInstalada());
  }), []);

  // ▶ No aparece si:
  //   · ya está instalada
  //   · el navegador no la ofrece (Firefox, Safari desktop, incógnito…)
  //   · la persona pulsó «Más tarde» en los últimos 7 días
  if (instalada || !puede || cerrada) return null;

  const aceptar = async () => {
    const ok = await instalar();
    setInstalada(yaInstalada());
    // Si dijo que no en el diálogo del navegador, honramos la decisión por 7
    // días también — insistir ahí sería lo que Chrome quitó de su mini-barra.
    if (!ok) {
      posponer();
      setCerrada(true);
    }
  };

  const despues = () => {
    posponer();
    setCerrada(true);
  };

  return (
    <div className="oferta-instalar" role="status">
      <span>{t('ver.puedeInstalar')}</span>
      <button type="button" className="btn btn--primary btn--sm" onClick={() => void aceptar()}>
        {t('ver.ahora')}
      </button>
      <button type="button" className="btn btn--ghost btn--sm" onClick={despues}>
        {t('ver.luego')}
      </button>
    </div>
  );
}
